// Dates in the selected language (TGCLOUD-1893). "about 5 hours ago" and
// "12 Sep 2026" stayed English whatever the language: moment's locale was
// never set and the relative times used date-fns without one.
//
// Other languages go through Intl (RelativeTimeFormat, DateTimeFormat): built
// into the browser, every language, no locale bundles to ship. English output
// is exactly what each screen wrote before. The configured numeric date format
// (Settings → Localisation, useDateFormatter) has no words in it and is not
// touched.

import { useCallback } from 'react';
import moment from 'moment';
import { formatDistance } from 'date-fns';
import { useLanguage } from 'src/hooks/useTranslate';
import {
  PSEUDO_LANGUAGE,
  intlLocale,
  isEnglish,
  pseudoLocalise
} from './language';

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Largest unit first; a span is written in the first unit it reaches.
const UNITS = [
  ['year', 365 * 86400],
  ['month', 30 * 86400],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1]
];

/**
 * How long ago (or until) `value` is, in `lang`: "5 hours ago", "in 2 days".
 * English is date-fns' wording, as before ("about 5 hours ago").
 *
 * @param {Date|string|number} value
 * @param {string} lang
 * @param {Date} [now]
 * @returns {string} '' for no date or an unreadable one
 */
export const timeAgo = (value, lang, now = new Date()) => {
  const date = toDate(value);
  if (!date) return '';
  if (isEnglish(lang) || lang === PSEUDO_LANGUAGE) {
    const english = formatDistance(date, now, { addSuffix: true });
    return lang === PSEUDO_LANGUAGE ? pseudoLocalise(english) : english;
  }
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const [unit, size] = UNITS.find(([, s]) => Math.abs(seconds) >= s) || [
    'second',
    1
  ];
  return new Intl.RelativeTimeFormat(intlLocale(lang), {
    numeric: 'auto'
  }).format(Math.round(seconds / size), unit);
};

/**
 * `value` with its month written out, in `lang`. `format` is the moment format
 * the screen uses in English ('D MMM YYYY', 'MMM YYYY', 'MMMM Do YYYY, h:mm a')
 * and is used as is in English; in another language the same parts (day,
 * month short or long, year, time) are written by Intl in that language's
 * order.
 *
 * @param {Date|string|number} value
 * @param {string} lang
 * @param {string} format moment tokens
 * @param {{ utc?: boolean }} [options] read and write the value in UTC
 * @returns {string} '' for no date or an unreadable one
 */
export const formatMonthDate = (value, lang, format, { utc = false } = {}) => {
  if (value === null || value === undefined || value === '') return '';
  const m = utc ? moment.utc(value) : moment(value);
  if (!m.isValid()) return '';
  if (isEnglish(lang)) return m.format(format);
  if (lang === PSEUDO_LANGUAGE) return pseudoLocalise(m.format(format));
  // Literal text in [brackets] is not a token.
  const tokens = format.replace(/\[[^\]]*\]/g, '');
  const options = {
    month: /MMMM/.test(tokens) ? 'long' : 'short',
    ...(/D|d/.test(tokens) ? { day: 'numeric' } : {}),
    ...(/Y/.test(tokens) ? { year: 'numeric' } : {}),
    ...(/[hH]/.test(tokens) ? { hour: 'numeric', minute: '2-digit' } : {}),
    ...(utc ? { timeZone: 'UTC' } : {})
  };
  return new Intl.DateTimeFormat(intlLocale(lang), options).format(m.toDate());
};

/**
 * `value` through `Date#toLocaleDateString` with `options`, in `lang`. English
 * keeps the browser's own locale, as every caller did before; another language
 * is written in that language rather than the browser's.
 *
 * @param {Date|string|number} value
 * @param {string} lang
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string} '' for no date or an unreadable one
 */
export const formatLocalDate = (value, lang, options) => {
  const date = toDate(value);
  if (!date) return '';
  if (isEnglish(lang)) return date.toLocaleDateString(undefined, options);
  if (lang === PSEUDO_LANGUAGE)
    return pseudoLocalise(date.toLocaleDateString(undefined, options));
  return date.toLocaleDateString(intlLocale(lang), options);
};

/** `timeAgo` in the current language. */
export const useTimeAgo = () => {
  const lang = useLanguage();
  return useCallback((value) => timeAgo(value, lang), [lang]);
};

/** `formatMonthDate` in the current language. */
export const useMonthDate = () => {
  const lang = useLanguage();
  return useCallback(
    (value, format, options) => formatMonthDate(value, lang, format, options),
    [lang]
  );
};
